import { getUserContext } from './userContext';
import { supabase } from './supabase';
import { getSubscriptionStatus, type SubscriptionStatus } from '@/services/subscriptionService';

/**
 * CTA Routing Utility
 * Handles smart routing logic for all CTAs across the application
 * Routes users based on authentication status and subscription state
 */

/**
 * Get route for "Start Now" / "Get Started" CTAs
 * Unauthenticated → /register → create account → /project-tracking-status-center
 * Authenticated → /project-tracking-status-center
 */
export async function getStartNowRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/register';
  }
  
  return '/project-tracking-status-center';
}

/**
 * Get route for "Start Your Subscription" CTAs
 * Unauthenticated → /pricing-plans-subscription-details
 * Authenticated → /subscription-management
 */
export async function getStartSubscriptionRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  // For authenticated users, check subscription status
  const session = await supabase.auth.getSession();
  if (session.data.session?.user) {
    const subscriptionStatus = await getSubscriptionStatus(session.data.session.user.id);
    
    if (subscriptionStatus.hasActiveSubscription || subscriptionStatus.hasInactiveSubscription) {
      return '/subscription-management';
    }
  }
  
  return '/pricing-plans-subscription-details';
}

/**
 * Get route for "Submit First Tasks" / "Submit Tasks" CTAs
 * Unauthenticated → /login?redirect=/request-submission-portal
 * Authenticated → /request-submission-portal
 */
export async function getSubmitTasksRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/login?redirect=/request-submission-portal';
  }
  
  return '/request-submission-portal';
}

/**
 * Get route for "Unlock Unlimited Support" CTAs
 * Unauthenticated → /pricing-plans-subscription-details
 * Authenticated → /subscription-management
 */
export async function getUnlockSupportRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  return '/subscription-management';
}

/**
 * Get route for "See Plans" / "Choose Plan" CTAs
 * Unauthenticated → /register
 * Authenticated / Active → /subscription-management
 */
export async function getSeePlansRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  // For authenticated users, check subscription status
  const session = await supabase.auth.getSession();
  if (session.data.session?.user) {
    const subscriptionStatus = await getSubscriptionStatus(session.data.session.user.id);
    
    if (subscriptionStatus.hasActiveSubscription) {
      return '/subscription-management';
    }
  }
  
  return '/pricing-plans-subscription-details';
}

/**
 * Get route for generic CTAs (Target Audience, Why Purple Cow sections)
 * Unauthenticated → /pricing-plans-subscription-details
 * Authenticated → /project-tracking-status-center
 */
export async function getGenericCTARoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  return '/project-tracking-status-center';
}

/**
 * Get route for "Start First 90 Days" CTAs
 * Unauthenticated → /pricing-plans-subscription-details
 * Authenticated → /subscription-management
 */
export async function getStartTimelineRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  return '/subscription-management';
}

/**
 * Get route for "Get Started With Unlimited Requests" CTAs
 * Unauthenticated → /pricing-plans-subscription-details
 * Authenticated → /subscription-management
 */
export async function getUnlimitedRequestsRoute(): Promise<string> {
  const context = await getUserContext();
  
  if (!context.isAuthenticated) {
    return '/pricing-plans-subscription-details';
  }
  
  return '/subscription-management';
}

/**
 * Get Calendly link with analytics tracking
 */
export function getCalendlyLink(): string {
  return 'https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call';
}

/**
 * Navigate to route (client-side)
 */
export function navigateToRoute(router: any, route: string) {
  router?.push(route);
}

/**
 * Open Calendly in new tab
 */
export function openCalendly() {
  window.open(getCalendlyLink(), '_blank');
}